import moment from 'moment';
import { ICorporateRegion } from './ICorporateRegion';
import { IHiringContract } from './HiringContract/IHiringContract';
import { IVendor } from './IVendor';
import { getDateFormat } from '../utils/dateHandler';
import { INotesDetails } from './INotesDetails';
import { EmptyGuid } from '../utils/Constants';

export enum FlightReportErrorMessages {
  DownForNightLocationErrorMessage = 'Down For Night Location is a required field',
  MeterStartErrorMessage = 'Only 1 decimal valid for Meter Start',
  MeterFinishErrorMessage = 'Only 1 decimal valid for Meter Finish',
  FlightReportDateErrorMessage = 'Flight date cannot be in the future',
  CorporateRegionErrorMessage = 'Forest Area is a required field',
  ContractRegistrationErrorMessage = 'Contract Registration is a required field',
  FlyingRegistrationErrorMessage = 'Flying Registration is a required field',
  MeterFinishLessThanStartErrorMessage = 'Meter Finish must be greater than or equal to Meter Start',
}
export function setFlightReportErrors(flightReport: IFlightReport) {
  flightReport.downForNightLocationError = !flightReport.downForNightLocation ? FlightReportErrorMessages.DownForNightLocationErrorMessage : '';
  flightReport.meterStartError =
    flightReport.meterStart &&
    !(flightReport.meterStart.toString().indexOf('.') === -1) &&
    flightReport.meterStart.toString().split('.')[1].length > 1
      ? FlightReportErrorMessages.MeterStartErrorMessage
      : '';
  flightReport.meterFinishError =
    flightReport.meterFinish &&
    !(flightReport.meterFinish.toString().indexOf('.') === -1) &&
    flightReport.meterFinish.toString().split('.')[1].length > 1
      ? FlightReportErrorMessages.MeterFinishErrorMessage
      : '';
  const meterStartValue = flightReport.meterStart;
  const meterFinishValue = flightReport.meterFinish;
  if (meterFinishValue && meterStartValue && meterFinishValue < meterStartValue) {
    flightReport.meterFinishError = FlightReportErrorMessages.MeterFinishLessThanStartErrorMessage;
  }
  flightReport.flightReportDateError =
    !flightReport.flightReportDate || new Date(flightReport.flightReportDate) > new Date()
      ? FlightReportErrorMessages.FlightReportDateErrorMessage
      : '';
  flightReport.corporateRegionError =
    !flightReport.corporateRegionId || flightReport.corporateRegionId === EmptyGuid ? FlightReportErrorMessages.CorporateRegionErrorMessage : '';
  flightReport.contractRegistrationError = !flightReport.contractRegistrationId ? FlightReportErrorMessages.ContractRegistrationErrorMessage : '';
  flightReport.flyingRegistrationError = !flightReport.flyingRegistrationId ? FlightReportErrorMessages.FlyingRegistrationErrorMessage : '';
}
export function getErrorMessages(flightReport: IFlightReport): string[] {
  setFlightReportErrors(flightReport);
  const errorMessages: string[] = [];
  if (flightReport.downForNightLocationError) {
    errorMessages.push(flightReport.downForNightLocationError);
  }
  if (flightReport.meterStartError) {
    errorMessages.push(flightReport.meterStartError);
  }
  if (flightReport.meterFinishError) {
    errorMessages.push(flightReport.meterFinishError);
  }
  if (flightReport.flightReportDateError) {
    errorMessages.push(flightReport.flightReportDateError);
  }
  if (flightReport.corporateRegionError) {
    errorMessages.push(flightReport.corporateRegionError);
  }
  if (flightReport.contractRegistrationError) {
    errorMessages.push(flightReport.contractRegistrationError);
  }
  if (flightReport.flyingRegistrationError) {
    errorMessages.push(flightReport.flyingRegistrationError);
  }
  if (flightReport.meterStart && flightReport.meterFinish) {
    const meterStartValue = flightReport.meterStart;
    const meterFinishValue = flightReport.meterFinish;
    if (meterFinishValue < meterStartValue) {
      errorMessages.push(FlightReportErrorMessages.MeterFinishLessThanStartErrorMessage);
    }
  }
  return errorMessages;
}
export interface IFlightReport {
  flightReportId: any;
  ao02Number: string;
  flightReportDate: string;

  meterFinish?: number;
  meterStart?: number;

  meterReadingReason: string;
  meterReadingNote: string;

  pointOfDeparture?: any;
  downForNightLocation?: string;
  noFlyDay: boolean;

  remark?: string;

  corporateRegionId: string;
  corporateRegionName: string;
  corporateRegion?: ICorporateRegion | undefined;

  // vendorId?:any,
  vendor?: IVendor | undefined;

  financeVendorId?: string;
  vendorName?: string;

  contractRegistrationId?: any;
  contractRegistration?: IHiringContract | undefined;
  contractType?: string;
  contractNumber?: string;

  aircraftType: string;
  aircraftSize: string;
  aircraftWingType: string;

  flyingRegistrationId?: any;
  flyingRegistrationName?: string;
  flyingRegistration?: IHiringContract | undefined;

  charternotelist?: INotesDetails[] | undefined;

  status?: string;
  pilotStatus: string;
  isFlagged: boolean;
  meterUS: boolean;
  unserviceableReport: boolean;
  isUnserviceableReportRefresh: boolean;
  deleted: boolean;

  hiringStartDate: any;
  hiringEndDate: any;
  pointOfHire: any;

  createUserId?: string;
  createTimestamp?: string;
  updateTimestamp?: string;
  updateUserId?: string;

  isMeterStartError?: boolean;
  isMeterFinishError?: boolean;

  dailyWorkPeriod: string;
  hasPdf: boolean;
  hasWFOPsEmailSuccess: boolean;

  pilotEmailId: string;
  hiringDetailsId?: number;

  downForNightLocationError?: string;
  meterStartError?: string;
  meterFinishError?: string;
  flyingRegistrationError?: string;
  contractRegistrationError?: string;
  corporateRegionError?: string;
  flightReportDateError?: string;
}

export class FlightReport implements IFlightReport, IErrorState<Partial<IFlightReport>> {
  readonly flightReportId: any;
  readonly ao02Number: string;
  readonly flightReportDate: string;

  meterFinish?: number;
  meterStart?: number;

  readonly meterReadingReason: string;
  readonly meterReadingNote: string;

  pointOfDeparture?: any;
  readonly downForNightLocation?: string;

  readonly noFlyDay: boolean;

  readonly remark?: string;

  readonly corporateRegionId: string = EmptyGuid;
  readonly corporateRegionName: string;
  corporateRegion?: ICorporateRegion;

  // readonly vendorId: any ;
  vendor?: IVendor;

  readonly financeVendorId: string;
  readonly vendorName: string;

  readonly contractRegistrationId?: any;
  contractRegistration?: IHiringContract;
  contractType?: string;
  contractNumber?: string;

  readonly aircraftType: string;
  readonly aircraftSize: string;
  readonly aircraftWingType: string;

  readonly flyingRegistrationId?: any;
  readonly flyingRegistrationName?: string;
  readonly flyingRegistration?: IHiringContract;

  readonly charternotelist?: INotesDetails[];

  readonly status: string;
  pilotStatus: string;
  isFlagged: boolean = false;
  readonly meterUS: boolean = false;
  readonly unserviceableReport: boolean = false;
  isUnserviceableReportRefresh: boolean = false;
  deleted: boolean = false;

  readonly hiringStartDate: any;
  readonly hiringEndDate: any;
  readonly pointOfHire: any;

  createUserId?: string;
  createTimestamp?: string;
  updateTimestamp?: string;
  updateUserId?: string;

  isMeterStartError?: boolean;
  isMeterFinishError?: boolean;

  dailyWorkPeriod: string;
  hasPdf: boolean;
  hasWFOPsEmailSuccess: boolean;

  pilotEmailId: string;
  hiringDetailsId?: number;

  downForNightLocationError?: string;
  meterStartError?: string;
  meterFinishError?: string;
  flyingRegistrationError?: string;
  contractRegistrationError?: string;
  corporateRegionError?: string;
  flightReportDateError?: string;

  constructor(from?: Partial<IFlightReport>) {
    this.flightReportId = from?.flightReportId ?? '';
    this.ao02Number = from?.ao02Number ?? '';
    this.flightReportDate = from?.flightReportDate ?? getDateFormat(from?.flightReportDate);

    this.meterFinish = from?.meterFinish;
    this.meterStart = from?.meterStart;

    this.aircraftType = from?.aircraftType ?? '';
    this.aircraftSize = from?.aircraftSize ?? '';
    this.aircraftWingType = from?.aircraftWingType ?? '';

    this.meterReadingReason = from?.meterReadingReason ?? '';
    this.meterReadingNote = from?.meterReadingNote ?? '';

    this.pointOfDeparture = from?.pointOfDeparture ?? null;
    this.downForNightLocation = from?.downForNightLocation ?? '';
    this.noFlyDay = from?.noFlyDay ?? false;

    this.remark = from?.remark ?? '';

    this.corporateRegionId = from?.corporateRegionId ?? EmptyGuid;
    this.corporateRegion = from?.corporateRegion ?? undefined;
    this.corporateRegionName = from?.corporateRegionName ?? '';
    // this.vendorId= from?.vendorId ?? null;
    this.vendor = from?.vendor ?? undefined;

    this.financeVendorId = from?.financeVendorId ?? '';
    this.vendorName = from?.vendorName ?? '';

    this.contractRegistrationId = from?.contractRegistrationId ?? null;
    this.contractRegistration = from?.contractRegistration ?? undefined;
    this.contractType = from?.contractType ?? '';
    this.contractNumber = from?.contractNumber ?? '';

    this.aircraftType = from?.aircraftType ?? '';
    this.aircraftSize = from?.aircraftSize ?? '';
    this.aircraftWingType = from?.aircraftWingType ?? '';

    this.flyingRegistrationId = from?.flyingRegistrationId ?? null;
    this.flyingRegistrationName = from?.flyingRegistrationName ?? '';
    this.flyingRegistration = from?.flyingRegistration ?? undefined;
    this.charternotelist = from?.charternotelist ?? undefined;

    this.status = from?.status ?? '';
    this.pilotStatus = from?.pilotStatus ?? '';
    this.isFlagged = from?.isFlagged ?? false;
    this.meterUS = from?.meterUS ?? false;
    this.unserviceableReport = from?.unserviceableReport ?? false;
    this.isUnserviceableReportRefresh = from?.isUnserviceableReportRefresh ?? false;
    this.deleted = from?.deleted ?? false;

    this.hiringStartDate = from?.hiringStartDate ?? null;
    this.hiringEndDate = from?.hiringEndDate ?? null;
    this.pointOfHire = from?.pointOfHire ?? null;

    this.createUserId = from?.createUserId ?? '';
    this.createTimestamp = getDateFormat(from?.createTimestamp);
    this.updateTimestamp = getDateFormat(from?.updateTimestamp);
    this.updateUserId = from?.updateUserId ?? '';

    this.isMeterStartError = from?.isMeterStartError ?? false;
    this.meterStartError = from?.meterStartError ?? '';
    this.isMeterFinishError = from?.isMeterFinishError ?? false;
    this.meterFinishError = from?.meterFinishError ?? '';
    this.dailyWorkPeriod = from?.dailyWorkPeriod ?? '';
    this.hasPdf = from?.hasPdf ?? false;
    this.hasWFOPsEmailSuccess = from?.hasWFOPsEmailSuccess ?? false;
    this.pilotEmailId = from?.pilotEmailId ?? '';
    this.hiringDetailsId = from?.hiringDetailsId;
  }

  get errorState() {
    return { flightReportDate: !this.flightReportDate ? true : false };
  }

  timeFormat(value?: string): string {
    if (!value) return '';

    return moment(value).isValid() ? moment(value).format('HH:mm') : value;
  }

  getTimeFormat(value: string): string {
    if (value) {
      if (moment(value).isValid()) {
        var time = moment(value).format('HH:mm');
        return time !== '00:00' ? time : '';
      } else {
        return value;
      }
    } else {
      return '';
    }
  }

  setStatus(value?: string): string {
    if (this.flightReportId === 0) return 'Created';
    else return '';
  }
}

export type ErrorState<T> = {
  [property in keyof T]: boolean;
};

export interface IErrorState<T> {
  errorState?: ErrorState<T>;
}
